<template>
  <div>
    <AM3InputLabel
      :label="label"
      :required="required"
      :input-id="dataTestId"
    />
    <div
      :id="id"
      class="relative text-sm w-full text-left px-3 h-11 items-center bg-surface-light hover:cursor-pointer pr-2 focus-within:outline-brand outline-1 outline  hover:border-brand  border rounded-md has-[[disabled]]:focus-within:outline-none has-[[disabled]]:hover:outline-transparent has-[[disabled]]:hover:border-transparent has-[[disabled]]:bg-surface has-[[disabled]]:cursor-not-allowed has-[[disabled]]:text-secondary flex flex-row"
      :class="isOpen ? 'border-brand outline-1 border-solid outline-brand' : 'border-surface-dark outline-transparent'"
    >
      <button
        ref="buttonRef"
        :data-test-id="generateDataTestId(dataTestId, 'selectbox-button')"
        :text="displayText"
        :disabled="disabled"
        class="text-left focus-visible:outline-none focus-visible:outline-transparent focus:outline-none disabled:bg-transparent size-full h-11 outline-none outline-transparent border-0"
        :name="`selectbox-${dataTestId || 'select'}`"
        :aria-label="ariaLabel || label || 'Select an option'"
        :aria-expanded="isOpen"
        :aria-haspopup="'listbox'"
        :aria-controls="isOpen ? `${id}-options` : undefined"
        :aria-activedescendant="focusedIndex >= 0 && isOpen ? `${id}-option-${focusedIndex}` : undefined"
        :class="displayText === props.placeholder ? 'text-secondary' : '' "
        role="combobox"
        @click="handleButtonClick"
        @keydown="handleKeydown"
      >
        {{ displayText }}
      </button>
      <AM3DropdownPanel
        :target="id"
        :model-value="isOpen"
        position="bottom"
        :stretch="true"
        @update:model-value="isOpen = $event"
      >
        <slot name="before-options" />

        <div
          :id="`${id}-options`"
          class="dropdown-menu-options w-full max-w-full pt-px "
          role="listbox"
          :aria-label="ariaLabel || label || 'Select an option'"
        >
          <button
            class="dropdown-menu-option outline-0 text-secondary outline-none text-left text-sm w-full py-2 px-4 pointer-events-none "
            disabled
          >
            {{ placeholder }}
          </button>
          <!-- for oop: option, index from options -->
          <button
            v-for="(option, index) in options"
            :id="`${id}-option-${index}`"
            :key="getKey(option, index)"
            class="dropdown-menu-option outline-none text-left text-sm w-full py-2 px-4 cursor-pointer"
            :class="[
              option.label === displayText ? 'font-bold text-brand  ' : '',
              isOptionFocused(index) ? 'bg-brand-light' : 'hover:bg-surface',
            ]"
            :data-test-id="generateDataTestId(dataTestId, 'option', getLabel(option))"
            :disabled="disabled"
            role="option"
            :aria-selected="(option.value === modelValue)"
            :tabindex="-1"
            @click="(event) => onClick(option, event)"
            @keyup.enter="(event) => onClick(option, event)"
          >
            <slot
              name="option"
              :option="option"
            >
              {{ getLabel(option) }}
            </slot>
          </button>

          <slot />
        </div>
        <slot name="after-options" />
      </AM3DropdownPanel>
      <div
        class="h-full flex flex-col items-center justify-center"
        @click="handleButtonClick"
      >
        <AM3Icon
          type="chevron-down"
          size="sm"
          class="duration-250 transition-transform "
          :class="isOpen ? 'rotate-180' : 'rotate-0'"
          aria-hidden="true"
          @click="handleButtonClick"
        />
      </div>
      <!-- <option
          v-if="placeholder"
          value=""
          disabled
        >
          {{ placeholder }}
        </option>
        <option
          v-for="option in options"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>  -->
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, toRef } from 'vue'
import AM3DropdownPanel from '@/components/am3-generic/base/AM3DropdownPanel.vue'
import AM3InputLabel from '@/components/am3-generic/inputs/AM3InputLabel.vue'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'
import { useKeyboardDropdownNavigation } from '@/composables/useKeyboardDropdownNavigation'

interface ISelectOption {
  value: string | number
  label: string
}

interface Props {
  modelValue: string | number
  label?: string
  required?: boolean
  ariaLabel?: string
  options: ISelectOption[]
  placeholder?: string
  disabled?: boolean
  dataTestId?: string
  displayValue?: string
}

interface Emits {
  (e: 'update:modelValue' | 'change', value: string | number): void
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: '',
  dataTestId: '',
  disabled: false,
  required: false,
})

const emit = defineEmits<Emits>()

// Reactive state
const isOpen = ref(false)
const buttonRef = ref<HTMLButtonElement | null>(null)
const id = computed(() => `selectbox-${props.dataTestId || 'select'}`)

// Keyboard navigation
const { focusedIndex, handleKeydown, openDropdown, selectOption, isOptionFocused } = useKeyboardDropdownNavigation({
  options: toRef(props, 'options'),
  modelValue: toRef(props, 'modelValue'),
  isOpen,
  disabled: toRef(props, 'disabled'),
  buttonRef,
  onSelect: (option: ISelectOption) => {
    emit('update:modelValue', option.value)
    emit('change', option.value)
  },
})

// Computed display text
const displayText = computed(() => {
  if (props.displayValue) return props.displayValue
  if (!props.modelValue) return props.placeholder
  const selectedOption = props.options.find(option => option.value === props.modelValue)
  return selectedOption ? (selectedOption.label) : props.modelValue.toString()
})

// Helper functions
const getKey = (option: ISelectOption, index: number) => {
  return option.value?.toString() || index.toString()
}

const getLabel = (option: ISelectOption) => {
  return option.label
}

// Event handlers
const handleButtonClick = openDropdown

const onClick = (option: ISelectOption, event: Event) => {
  event.preventDefault()
  // Ensure we have a valid label for the composable
  const optionWithLabel = {
    ...option,
    label: getLabel(option) || option.value.toString(),
  }
  selectOption(optionWithLabel)
}
</script>
